import { useState, useRef, useEffect } from "react";
import {
    FaSearch, FaShoppingCart,
    FaUser, FaSignInAlt, FaHeart, FaSignOutAlt,
    FaUserCircle, FaHistory
} from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./navbar.css";
import AadviLogo from "../assets/Aadvi-logo.png";
import { getFullImageUrl } from "../api";
import { useNotificationManager } from "../utils/notificationManager";
import { toast } from "react-hot-toast";
import { Modal, Button } from "react-bootstrap";
import { apiFetch } from "../api";
import ProductModal from "./ProductModal";
import { clearCart } from "../redux/cartSlice";
import { clearWishlist } from "../redux/WishlistSlice";

const Navbar = ({ searchQuery: globalSearchQuery, setSearchQuery: setGlobalSearchQuery }) => {
    // State variables
    const [isMenuOpen] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [, setCartNotification] = useState(null);
    const [, setWishlistNotification] = useState(null);
    const [cartHover, setCartHover] = useState(false);
    const [wishlistHover, setWishlistHover] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
    const [userName, setUserName] = useState("Guest");
    const [userImage, setUserImage] = useState(null);

    // Redux and Router hooks
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const cart = useSelector((state) => state.cart);
    const wishlist = useSelector((state) => state.wishlist);
    const cartTotal = cart.total;
    const isUserLoggedIn = !!localStorage.getItem("token");
    // Refs for click outside detection
    const wishlistButtonRef = useRef(null);
    const wishlistDropdownRef = useRef(null);
    const cartButtonRef = useRef(null);
    const cartDropdownRef = useRef(null);
    const userButtonRef = useRef(null);
    const userDropdownRef = useRef(null);
    const mobileUserButtonRef = useRef(null);
    const mobileUserDropdownRef = useRef(null);
    const searchRef = useRef(null);

    // Update user name and image on login state change or location change
    useEffect(() => {
        if (isUserLoggedIn) {
            try {
                const userString = localStorage.getItem("user");
                const userData = JSON.parse(userString);
                // Set both name and image on initial load / route change
                const name = userData?.name || (userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : userData?.email) || 'User';
                setUserName(name);
                const imgPath = userData?.profileImage || userData?.profilePic || userData?.profile_image || userData?.avatar || userData?.image || userData?.imageUrl || (Array.isArray(userData?.images) ? userData.images[0] : null) || null;
                if (imgPath) setUserImage(getFullImageUrl(imgPath)); else setUserImage(null);
            } catch (error) {
                console.error("Failed to parse user data:", error);
                setUserName("User");
                setUserImage(null);
            }
        } else {
            setUserName("Guest");
            setUserImage(null);
        }
    }, [isUserLoggedIn, location]);

    // Listen for cross-tab and same-tab user updates
    useEffect(() => {
        const applyUserData = (userData) => {
            try {
                const user = userData || JSON.parse(localStorage.getItem('user'));
                if (user) {
                    const name = user.name || (user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user.email) || 'User';
                    setUserName(name);
                    // profile image - check multiple possible field names/shapes
                    const imgPath = user.profileImage || user.profilePic || user.profile_image || user.avatar || user.image || user.imageUrl || (Array.isArray(user.images) ? user.images[0] : null) || user.photo || null;
                    if (imgPath) {
                        setUserImage(getFullImageUrl(imgPath));
                    } else {
                        setUserImage(null);
                    }
                } else {
                    setUserName('Guest');
                    setUserImage(null);
                }
            } catch (err) {
                setUserName('User');
                setUserImage(null);
            }
        };
        // Event handlers 
        const onUserUpdated = (e) => {
            applyUserData(e?.detail || null);
        };

        const onStorage = (e) => {
            // Handle storage events for user data changes
            if (!e) return;
            if (e.key === 'user' || e.key === 'user_updated_at') {
                applyUserData(null);
            }
        };
        // Register event listeners
        window.addEventListener('userUpdated', onUserUpdated);
        window.addEventListener('storage', onStorage);

        // Apply current localStorage user to initialize state immediately
        applyUserData(null);

        return () => {
            // Cleanup event listeners
            window.removeEventListener('userUpdated', onUserUpdated);
            window.removeEventListener('storage', onStorage);
        };
    }, []);

    // Logout confirmation
    const handleLogoutConfirm = () => {
        setIsUserMenuOpen(false);
        setShowLogoutModal(true);
    };

    const handleLogout = () => {
        // Step 1: Clear cart and wishlist from Redux state
        dispatch(clearCart());
        dispatch(clearWishlist());

        // Step 2: Clear user token and info from localStorage
        localStorage.removeItem("token");
        localStorage.removeItem("user");

        // Update navbar UI immediately
        setUserImage(null);
        setUserName('Guest');

        // Step 3: Close modal and show notification
        setShowLogoutModal(false);
        toast.success("Logged out successfully!");

        // Step 4: Redirect to login page
        navigate("/login");
    };

    // Nav items
    const menuItems = [
        { name: "Home", link: "/" },
        { name: "Shop", link: "/products" },
        { name: "About", link: "/about" },
        { name: "Contact", link: "/contact" },
    ];

    // Search handler
    const handleSearch = (e) => {
        const query = e.target.value;
        setGlobalSearchQuery(query);
        const normalize = (str) => str.toLowerCase().replace(/\s/g, "");
        const normalizedQuery = normalize(query);

        // If search query is empty, clear results and hide dropdown
        if (normalizedQuery === "") {
            setSearchResults([]);
            setShowSearchResults(false);
            return;
        }
        // Filter products based on normalized search query
        const filtered = products.filter((product) => normalize(product.name).includes(normalizedQuery));
        setSearchResults(filtered);
        setShowSearchResults(true);
    };

    useEffect(() => {
        // Fetch products once on component mount or location change
        const fetchProducts = async () => {
            try {
                const data = await apiFetch("/api/products/all");
                if (data && data.data) {
                    setProducts(data.data);
                } else {
                    setProducts([]);
                }
            } catch (error) {
                setProducts([]);
            }
        };
        fetchProducts();
    }, [location]);

    useEffect(() => {
        // Handle bottom search open event to close search results
        const handleBottomSearch = (e) => {
            if (e.detail.open) {
                setShowSearchResults(false);
                setGlobalSearchQuery("");
            }
        };
        // Register event listener
        window.addEventListener('bottomSearchOpen', handleBottomSearch);
        return () => window.removeEventListener('bottomSearchOpen', handleBottomSearch);
    }, [setGlobalSearchQuery]);
    // Notification manager hooks
    useNotificationManager(setCartNotification, setWishlistNotification);

    useEffect(() => {
        // Close dropdowns and menus on route change
        setCartHover(false);
        setWishlistHover(false);
        setIsUserMenuOpen(false);
        setShowSearchResults(false);
    }, [location]);

    useEffect(() => {
        // Close dropdowns and menus on scroll
        const handleScroll = () => {
            setCartHover(false);
            setWishlistHover(false);
            setIsUserMenuOpen(false);
            setShowSearchResults(false);
        };
        // Register scroll event listener
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        // Click outside detection to close dropdowns and menus
        const handleClickOutside = (event) => {
            if (
                // Check if click is outside all relevant dropdowns and buttons
                cartButtonRef.current && !cartButtonRef.current.contains(event.target) &&
                cartDropdownRef.current && !cartDropdownRef.current.contains(event.target) &&
                wishlistButtonRef.current && !wishlistButtonRef.current.contains(event.target) &&
                wishlistDropdownRef.current && !wishlistDropdownRef.current.contains(event.target) &&
                userButtonRef.current && !userButtonRef.current.contains(event.target) &&
                userDropdownRef.current && !userDropdownRef.current.contains(event.target) &&
                //Checking the new mobileUserButtonRef
                mobileUserButtonRef.current && !mobileUserButtonRef.current.contains(event.target) &&
                mobileUserDropdownRef.current && !mobileUserDropdownRef.current.contains(event.target) &&
                searchRef.current && !searchRef.current.contains(event.target)
            ) {
                // Close all dropdowns and menus
                setCartHover(false);
                setWishlistHover(false);
                setIsUserMenuOpen(false);
                setShowSearchResults(false);
            }
        };
        // Register document mousedown event listener
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const closeAllModals = () => {
        // Close all modals and search results
        setIsModalOpen(false);
        setShowSearchResults(false);
        setGlobalSearchQuery("");
    };

    return (
        <>
            <nav
                className="navbar navbar-expand-lg navbar-dark text-light shadow"
                style={{
                    backgroundColor: "#82278A", display: "block",
                    position: "sticky", top: 0, zIndex: 1005,
                }}
            >
                <div className="container-fluid" >
                    <img
                        className="navbar-brand nav-img fw-bold rounded-circle"
                        src={AadviLogo} height="70" width="70" alt="Logo"
                        onClick={() => setIsLogoModalOpen(true)}
                        style={{ cursor: 'pointer' }}
                    />
                    <div className="dropdown position-relative d-md-none">
                         <div
                            className="d-flex flex-column align-items-center"
                            style={{ cursor: 'pointer' }}
                            ref={mobileUserButtonRef}
                            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                        >
                            <button
                                className="navbar-toggler border-0"
                                type="button"
                                style={{ padding: 0 }} 
                            >
                                {userImage ? (
                                    <img src={userImage} alt="Profile" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
                                ) : (
                                    <FaUserCircle className="text-white" style={{ fontSize: '1.5rem' }} />
                                )}
                            </button>
                            <span className="small text-light" style={{ fontSize: '12px', marginTop: '0px' }}>
                                {userName}
                            </span>
                        </div>

                        {isUserMenuOpen && (
                            <ul
                                ref={mobileUserDropdownRef}
                                className="dropdown-menu show user-dropdown"
                                >
                                <button
                                    aria-label="Close user menu"
                                    className="dropdown-close"
                                    onClick={() => setIsUserMenuOpen(false)}
                                >
                                    X
                                </button>
                                {isUserLoggedIn ? (
                                    <>
                                        <li>
                                            <Link
                                                className="dropdown-item d-flex align-items-center"
                                                to="/profile"
                                                onClick={() => setIsUserMenuOpen(false)}
                                            >
                                                <FaUserCircle className="me-2" /> User Profile
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                className="dropdown-item d-flex align-items-center"
                                                to="/orderhistory"
                                                onClick={() => setIsUserMenuOpen(false)}
                                            >
                                                <FaHistory className="me-2" /> Order History
                                            </Link>
                                        </li>
                                        <li><hr className="dropdown-divider" /></li>
                                        <li>
                                            <button
                                                className="dropdown-item text-danger d-flex align-items-center logout-button"
                                                onClick={handleLogoutConfirm}
                                            >
                                                <FaSignOutAlt className="me-2" /> Logout
                                            </button>
                                        </li>
                                    </>
                                ) : (
                                    <li>
                                        <Link
                                            to="/login"
                                            className="dropdown-item d-flex align-items-center"
                                            onClick={() => setIsUserMenuOpen(false)}
                                        >
                                            <FaSignInAlt className="me-2" /> Login
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        )}
                    </div>
                    <div className={`collapse navbar-collapse ${isMenuOpen ? "show" : ""}`}>
                        <ul className="navbar-nav me-auto mb-2 mb-lg-0">
                            {menuItems.map((item) => (
                                <li key={item.name} className="nav-item">
                                    <Link to={item.link} className="nav-link text-light fw-semibold">
                                        {item.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                        <div className="d-flex align-items-center">
                            <form className="d-flex position-relative me-3">
                                <span className="position-absolute top-50 start-0 translate-middle-y ps-3 text-muted">
                                    <FaSearch />
                                </span>
                                <input
                                    className="form-control ps-5 rounded-pill"
                                    type="search"
                                    placeholder="Search products..."
                                    value={globalSearchQuery}
                                    onChange={handleSearch}
                                />
                                {showSearchResults && globalSearchQuery.trim() && (
                                    <div
                                        ref={searchRef}
                                        className="bottom-search-results"
                                        style={{ zIndex: 1000, top: "100%" }}
                                    >
                                        {searchResults.length > 0 ? (
                                            searchResults.map((result) => (
                                                <div
                                                    key={result._id}
                                                    className="search-result-item"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => {
                                                        setSelectedProduct(result);
                                                        setIsModalOpen(true);
                                                    }}
                                                >
                                                    {result.name}
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{ color: "red", padding: "8px" }}>
                                                No Matches Found
                                            </div>
                                        )}
                                    </div>
                                )}
                            </form>
                            <div className="nav-item dropdown me-3 position-relative d-none d-md-flex">
                                <div
                                    className="d-flex flex-column align-items-center"
                                    style={{ cursor: 'pointer' }}
                                    ref={wishlistButtonRef}
                                    onMouseEnter={() => setWishlistHover(true)}
                                    onMouseLeave={() =>
                                        setTimeout(() => {
                                            if (
                                                !wishlistDropdownRef.current?.matches(":hover") &&
                                                !wishlistButtonRef.current?.matches(":hover")
                                            )
                                                setWishlistHover(false);
                                        }, 100)
                                    }
                                >
                                    <button
                                        className="btn btn-outline-light rounded-circle position-relative d-flex align-items-center justify-content-center cart-btn"
                                        style={{ width: "40px", height: "40px" }}
                                        onClick={() => navigate("/wishlist")}
                                    >
                                        <FaHeart />
                                        {wishlist.itemCount > 0 && (
                                            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                                                {wishlist.itemCount}
                                            </span>
                                        )}
                                    </button>
                                    <span
                                        className="small text-light"
                                        style={{ fontSize: '12px', marginTop: '2px', display:'flex', alignItems:'center', justifyContent:'center' }}
                                        onClick={() => navigate("/wishlist")}
                                    >
                                        Wishlist
                                    </span>
                                </div>
                                {wishlistHover && wishlist.items.length > 0 && (
                                    <div
                                        ref={wishlistDropdownRef}
                                        className="dropdown-menu dropdown-menu-end show p-3"
                                        style={{
                                            width: "300px", right: 0,
                                            left: "auto", top: "100%",
                                        }}
                                        onMouseEnter={() => setWishlistHover(true)}
                                        onMouseLeave={() =>
                                            setTimeout(() => {
                                                if (
                                                    !wishlistButtonRef.current?.matches(":hover") &&
                                                    !wishlistDropdownRef.current?.matches(":hover")
                                                )
                                                    setWishlistHover(false);
                                            }, 100)
                                        }
                                    >
                                        <h6 className="fw-bold mb-2">
                                            Your Wishlist ({wishlist.itemCount} items)
                                        </h6>
                                    </div>
                                )}
                            </div>
                            <div className="nav-item dropdown me-3 position-relative d-none d-md-flex">
                                <div
                                    className="d-flex flex-column align-items-center"
                                    style={{ cursor: 'pointer' }}
                                    ref={cartButtonRef}
                                    onMouseEnter={() => setCartHover(true)}
                                    onMouseLeave={() =>
                                        setTimeout(() => {
                                            if (
                                                !cartDropdownRef.current?.matches(":hover") &&
                                                !cartButtonRef.current?.matches(":hover")
                                            )
                                                setCartHover(false);
                                        }, 100)
                                    }
                                >
                                    <button
                                        className="btn cart-btn btn-warning position-relative rounded-circle d-flex align-items-center justify-content-center"
                                        style={{ width: "40px", height: "40px" }}
                                        onClick={() => navigate("/cart")}
                                    >
                                        <FaShoppingCart />
                                        {cart.itemCount > 0 && (
                                            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                                                {cart.itemCount}
                                            </span>
                                        )}
                                    </button>
                                    <span
                                        className="small text-light"
                                        style={{ fontSize: '12px', marginTop: '2px', display:'flex', alignItems:'center', justifyContent:'center' }}
                                        onClick={() => navigate("/cart")}
                                    >
                                        Cart
                                    </span>
                                </div>
                                {cartHover && cart.items.length > 0 && (
                                    <div
                                        ref={cartDropdownRef}
                                        className="dropdown-menu dropdown-menu-end show p-3"
                                        style={{
                                            width: "300px", right: 0,
                                            left: "auto", top: "100%",
                                        }}
                                        onMouseEnter={() => setCartHover(true)}
                                        onMouseLeave={() =>
                                            setTimeout(() => {
                                                if (
                                                    !cartButtonRef.current?.matches(":hover") &&
                                                    !cartDropdownRef.current?.matches(":hover")
                                                )
                                                    setCartHover(false);
                                            }, 100)
                                        }
                                    >
                                        <h6 className="fw-bold mb-2">
                                            Your Cart ({cart.itemCount} items)
                                        </h6>
                                        <hr className="dropdown-divider" />
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="fw-bold">Total:</span>
                                            <span className="fw-bold">₹{(cartTotal || 0).toFixed(2)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mt-2">
                                            <Link
                                                to="/cart"
                                                className="btn btn-sm text-light"
                                                style={{ background: "#82278A" }}
                                                onClick={() => setCartHover(false)}
                                            >
                                                View Cart
                                            </Link>
                                            <Link
                                                to="/checkout"
                                                className="btn btn-sm btn-success"
                                                onClick={() => setCartHover(false)}
                                            >
                                                Checkout
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="dropdown position-relative">
                                <div
                                    className="d-flex flex-column align-items-center"
                                    style={{ cursor: 'pointer' }}
                                    ref={userButtonRef}
                                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                >
                                        <button
                                            className="btn btn-outline-light rounded-circle d-flex align-items-center justify-content-center"
                                            style={{ width: "40px", height: "40px", overflow: 'hidden', padding: 0 }}
                                        >
                                            {userImage ? (
                                                <img src={userImage} alt="Profile" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                                            ) : (
                                                <FaUser />
                                            )}
                                        </button>
                                    <span className="small text-light" style={{ fontSize: '12px', marginTop: '2px' }}>
                                        {userName}
                                    </span>
                                </div>
                                {isUserMenuOpen && (
                                    <ul
                                        ref={userDropdownRef}
                                        className="dropdown-menu show user-dropdown"
                                        style={{
                                            position: "absolute", top: "100%",
                                            right: 0, left: "auto", minWidth: "180px",
                                        }}
                                    >
                                        <button
                                            aria-label="Close user menu"
                                            className="dropdown-close"
                                            onClick={() => setIsUserMenuOpen(false)}
                                        >
                                            X
                                        </button>
                                        {isUserLoggedIn ? (
                                            <>
                                                <li>
                                                    <Link
                                                        className="dropdown-item d-flex align-items-center"
                                                        to="/profile"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                    >
                                                        <FaUserCircle className="me-2" /> User Profile
                                                    </Link>
                                                </li>
                                                <li>
                                                    <Link
                                                        className="dropdown-item d-flex align-items-center"
                                                        to="/orderhistory"
                                                        onClick={() => setIsUserMenuOpen(false)}
                                                    >
                                                        <FaHistory className="me-2" /> Order History
                                                    </Link>
                                                </li>
                                                <li><hr className="dropdown-divider" /></li>
                                                <li>
                                                    <button
                                                        className="dropdown-item text-danger d-flex align-items-center logout-button"
                                                        onClick={handleLogoutConfirm}
                                                    >
                                                        <FaSignOutAlt className="me-2" />
                                                        <span className="logout-text">Logout</span>
                                                    </button>
                                                </li>
                                            </>
                                        ) : (
                                            <li>
                                                <Link
                                                    to="/login"
                                                    className="dropdown-item d-flex align-items-center"
                                                    onClick={() => setIsUserMenuOpen(false)}
                                                >
                                                    <FaSignInAlt className="me-2" /> Login
                                                </Link>
                                            </li>
                                        )}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
            <Modal show={showLogoutModal} onHide={() => setShowLogoutModal(false)} centered className="logout-modal">
                <Modal.Header closeButton><Modal.Title>Confirm Logout</Modal.Title></Modal.Header>
                <Modal.Body><p>Are you sure you want to log out?</p></Modal.Body>
                <Modal.Footer className="confirm-actions-navbar" style={{ borderTop: 'none' }}>
                    <Button variant="secondary" onClick={() => setShowLogoutModal(false)} className="confirm-btn-navbar">Cancel</Button>
                    <Button variant="danger" onClick={handleLogout} className="confirm-btn-navbar" style={{backgroundColor:'rgb(130, 39, 138)'}}>Log Out</Button>
                </Modal.Footer>
            </Modal>
            <Modal show={isLogoModalOpen} onHide={() => setIsLogoModalOpen(false)} centered size="sm">
                <Modal.Body className="p-0 text-center bg-transparent">
                    <img
                        src={AadviLogo} alt="Aadvi Logo"
                        style={{ width: '100%', height: 'auto', objectFit: 'contain', borderRadius: '10%' }}
                    />
                </Modal.Body>
            </Modal>
            <ProductModal post={selectedProduct} isOpen={isModalOpen} onClose={closeAllModals} />
            <style>{`.logout-button:focus { background-color: #f8d7da; color: #82278A; }`}</style>
        </>
    );
};
export default Navbar;